<template>
  <div v-show="shown" class="pictures-editor">
    <CloseButton :white="true" style="left:10px" @click.native="done()" />
    <div class="all-pictures f-h">
      <div class="pictures-wrapper">
        <div
          v-for="(picture, index) in pictures"
          class="img-wrapper f-w"
          :class="{ selected: selectedPicture == picture }"
          @click="selectPicture(index)"
        >
          <img class="min f-w" alt="" :src="picture.data">
        </div>
        <img
          class="min f-w"
          alt="Add"
          src="/images/add-white-big.png"
          @click="addPicture()"
        >
      </div>
      <input
        ref="pictures"
        type="file"
        style="opacity:0;position:absolute;z-index:-1;width: 0px;"
        multiple
        accept=".png,.jpg,.bmp,.gif,.jpeg"
        @change="backFromBrowsing"
      >
    </div>
    <div class="picture-edition f-h">
      <div class="f-h">
        <div ref="wrapper" :class="pictureWrapperClasses">
          <canvas ref="savingCanvas" class="picture-edition__saving" />
          <canvas
            v-show="selectedPictureData"
            ref="imageCanvas"
            class="picture-edition__image"
          />
          <canvas v-drawable-canvas="onDraw" ref="drawCanvas" class="picture-edition__drawing" />
          <div v-show="pictureLoading" class="picture-edition__loading">
            <img class="picture-edition__loading__logo" src="/images/logo300.png" alt="Loading...">
          </div>
        </div>
        <div class="buttons-container">
          <button class="action-btn img-btn" :disabled="!anyEdit" @click="undo()">
            <img src="/images/cancel-white.png" alt="">
          </button>
          <button class="action-btn img-btn ml10" @click="rotatePicture()">
            <img src="@/assets/img/rotate-bold-white.png" alt="">
          </button>
          <button class="action-btn img-btn red ml50" :disabled="disabled" @click="removePicture()">
            <img src="/images/delete-white.png" alt="">
          </button>
          <button class="action-btn img-btn green ml10" @click="done()">
            <img src="/images/tick-white.png" alt="">
          </button>
        </div>
      </div>
    </div>
    <!-- Hidden components -->
    <PictureTaker
      v-if="pictureTakerVisibility"
      @close="pictureTakerVisibility = false"
      @picture="pictureTaken"
    />
  </div>
</template>

<script>
/**
 * Component used as a page overlay to edit an array of pictures.
 * Allow adding, editing and removing pictures
 */
import $ from 'jquery';
// Import components
import CloseButton from '@/components/close-button.vue';
import PictureTaker from '@/components/picture-taker.vue';
import directiveDrawableCanvas from '@/directives/drawable-canvas';
// Import utils
import { rotateCanvasImage, handlePictureOrientation, handlePictureDimension } from '@/utils/file';
import { calert, calertHide, detectEscapeKey } from '@/utils/main';

// Capture file type of a file data string
const RE_FILE_TYPE = /^data:(.*);/;

export default {
  name: 'PicturesEditor',
  components: {
    CloseButton,
    PictureTaker,
  },
  directives: {
    DrawableCanvas: directiveDrawableCanvas,
  },
  props: {
    disabled: Boolean,
  },
  data() {
    return {
      shown: false,
      pictures: [],
      selectedIndex: null,
      nextSelectedIndex: null,
      anyEdit: false,
      isWrapperWider: false,
      pictureLoading: false,
      pictureTakerVisibility: false,
    };
  },
  computed: {
    selectedPicture() {
      return this.pictures[this.selectedIndex] || null;
    },
    selectedPictureData() {
      return this.selectedPicture ? this.selectedPicture.data : null;
    },
    selectedPictureType() {
      if (this.selectedPicture instanceof Blob) {
        return this.selectedPicture.type;
      }
      const match = RE_FILE_TYPE.exec(this.selectedPictureData);
      return match ? match[1] : null;
    },
    pictureWrapperClasses() {
      return {
        'picture-edition__wrapper': true,
        'picture-edition__wrapper--taller': !this.isWrapperWider,
        'picture-edition__wrapper--wider': !!this.isWrapperWider,
      };
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeAction, false);
    window.addEventListener('orientationchange', this.resizeAction, false);
    const { savingCanvas, imageCanvas, drawCanvas } = this.$refs;
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeAction, false);
    window.removeEventListener('orientationchange', this.resizeAction, false);
  },
  methods: {
    onDraw() {
      this.anyEdit = true;
      this.selectedPicture.edited = true;
    },
    /**
     * Re-evaluate `this.isWrapperWider` value ;
     * Evaluate wrapper ideal size for displaying content with correct ratio
     * @param {Number} childWidth - Width of the wrapper's child
     * @param {Number} childHeight - Height of the wrapper's child
     */
    evalWrapperFit(childWidth, childHeight) {
      const { wrapper } = this.$refs;
      if (!wrapper) return;

      const parentHeight = wrapper.clientHeight;
      const parentWidth = wrapper.clientWidth;
      this.isWrapperWider = (!childHeight || childWidth / childHeight > parentWidth / parentHeight);
    },
    /**
     * Update canvas and image display
     * Need to be call after any wrapper/image/canvas resize (image change included)
     */
    resizeAction() {
      const { imageCanvas } = this.$refs;
      if (!this.shown || !imageCanvas) return;

      const { height, width } = imageCanvas;
      this.evalWrapperFit(width, height);
    },
    /**
     * Clear drawing canvas image
     */
    clearDrawing() {
      const { drawCanvas } = this.$refs;
      if (!drawCanvas) return;
      const ctx = drawCanvas.getContext('2d');
      ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
      this.anyEdit = false;
    },
    /**
     * Combine and save the drawing canvas image with the current selected picture
     * @return {Promise} promise, resolved when the canvas finished saving
     */
    async saveDrawing() {
      const { drawCanvas, imageCanvas, savingCanvas } = this.$refs;
      const picture = this.selectedPicture;
      if (!drawCanvas || !imageCanvas || !savingCanvas
        || !picture || !this.anyEdit) {
        return Promise.resolve();
      }
      this.pictureLoading = true;
      savingCanvas.width = imageCanvas.width;
      savingCanvas.height = imageCanvas.height;
      const ctx = savingCanvas.getContext('2d');
      ctx.drawImage(imageCanvas, 0, 0);
      ctx.drawImage(drawCanvas, 0, 0);
      this.clearDrawing();
      const pictureType = this.selectedPictureType;
      return new Promise((resolve) => {
        // Since toDataURL clog the JS execution, isolate it with `setTimeout` seems to
        // resolve the issue. The loading logo on save will never been displayed without this.
        setTimeout(() => {
          // Warning: toDataURL is pretty slow, especially with big images
          // TODO Use .toBlob
          picture.data = savingCanvas.toDataURL(pictureType);
          savingCanvas.width = 0;
          savingCanvas.height = 0;
          this.pictureLoading = false;
          resolve();
        }, 0);
      });
    },
    /** Initialize */
    show() {
      this.shown = true;
      this.$refs.pictures.value = '';
      detectEscapeKey(this.done);
      if (this.pictures.length == 0) {
        this.addPicture();
      } else {
        this.selectPicture(0);
      }
    },
    /**
     * Change picture displayed (without saving/clearing)
     * @param {Number} index - Picture index in `this.pictures`
     */
    displayPicture(index) {
      this.selectedIndex = Math.min(Math.max(0, index), this.pictures.length - 1);
      this.loadImage();
    },
    /** Select the picture to allow drawing or deleting */
    selectPicture(index) {
      this.nextSelectedIndex = index;
      if (this.pictureLoading) {
        // Already trying to save an image
        return;
      }
      this.saveDrawing()
        .then(() => {
          this.displayPicture(this.nextSelectedIndex);
        });
    },
    loadImage() {
      if (!this.selectedPictureData) return null;
      const image = new Image();
      image.onload = () => {
        const { imageCanvas, drawCanvas } = this.$refs;
        imageCanvas.height = image.naturalHeight;
        imageCanvas.width = image.naturalWidth;
        drawCanvas.height = image.naturalHeight;
        drawCanvas.width = image.naturalWidth;
        const ctx = imageCanvas.getContext('2d');
        ctx.drawImage(image, 0, 0);
        this.resizeAction();
      };
      image.src = this.selectedPictureData;
      return image;
    },
    removePicture() {
      if (!this.disabled && this.selectedPicture) {
        // Remove from server
        if (this.selectedPicture.id != 0) {
          this.$emit('removePicture', this.selectedPicture.id);
        }
        // Remove from local container
        this.pictures.splice(this.selectedIndex, 1);
        this.$nextTick(() => {
          this.displayPicture(this.selectedIndex);
        });
        this.clearDrawing();
      }
    },
    /**
     * Rotate selected picture and drawing canvas
     * @param {Number} rotateFactor - Number of rotation by 90 degree (by ROTATE_INCR constant)
     */
    rotatePicture(rotateFactor) {
      const { imageCanvas, drawCanvas } = this.$refs;
      rotateCanvasImage(imageCanvas, rotateFactor);
      rotateCanvasImage(drawCanvas, rotateFactor);
      this.selectedPicture.edited = true;
      this.anyEdit = true;
      this.resizeAction();
    },
    /** Save the picture and end the edition */
    done() {
      this.saveDrawing()
        .then(() => {
          this.$emit('editionDone', this.pictures);
        });
      this.shown = false;
      this.selectedIndex = -1;
    },
    /**
     * Restore initial image ; Cancel modification
     */
    undo() {
      this.clearDrawing();
      this.loadImage();
      if (this.selectedPicture) {
        this.selectedPicture.edited = false;
      }
      this.anyEdit = false;
    },
    /** 5 following methods allow the user to add images */
    addPicture() {
      if (!this.disabled) {
        calert(
          this.$t('message.HOW_ADD_PICTURE'),
          [
            { title: this.$t('message.TAKE_PICTURE'), action: this.takePicture },
            { title: this.$t('message.BROWSE_GALLERY'), action: this.browseGallery },
          ],
          false,
          '',
          [{ pasteonly: true }],
          () => {
            $(document).off('paste.paste_picture_editor');
          },
        );
        $(document).on('paste.paste_picture_editor', ({ clipboardData, originalEvent }) => {
          const items = (clipboardData || originalEvent.clipboardData).items;
          Object.values(items).forEach((item) => {
            if (item.kind === 'file') {
              calertHide();
              $(document).off('paste.paste_picture_editor');
              handlePictureOrientation(item.getAsFile())
                .then(blob => handlePictureDimension(blob, 1100, 1100))
                .then((blob) => {
                  const reader = new FileReader();
                  reader.addEventListener('load', () => {
                    if (reader.error) {
                      console.error(reader.error);
                      return;
                    }
                    blob.data = reader.result;
                    blob.id = 0;
                    this.pictures.push(blob);
                  }, false);
                  reader.readAsDataURL(blob);
                });
            }
          });
        });
      }
    },
    takePicture() {
      this.pictureTakerVisibility = true;
    },
    pictureTaken(blob) {
      this.pictureTakerVisibility = false;
      this.pictures.push({ id: 0, data: URL.createObjectURL(blob), edited: true });
    },
    browseGallery() {
      $(this.$refs.pictures).trigger('click');
    },
    backFromBrowsing() {
      // Read device's files
      const pictures = this.$refs.pictures.files;
      const imageType = /^image\//;
      for (let i = 0, len = pictures.length; i < len; i++) {
        const file = pictures[i];
        if (!imageType.test(file.type)) {
          continue;
        }
        /* eslint-disable no-loop-func */
        handlePictureOrientation(file)
          .then(blob => handlePictureDimension(blob, 1100, 1100))
          .then((blob) => {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
              if (reader.error) {
                console.error(reader.error);
                return;
              }
              blob.data = reader.result;
              blob.id = 0;
              this.pictures.push(blob);
            }, false);
            reader.readAsDataURL(blob);
          });
        /* eslint-enable no-loop-func */
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.pictures-editor {
  position: fixed;
  top: $header-height;
  left: $nav-width;
  background: rgba(0, 0, 0, 0.87);
  z-index: 10017;
  padding: 10px 5px;
  box-sizing: border-box;
  overflow: hidden;
  white-space: initial; /* Prevent any page from applying nowrap */
  bottom: 0px;
  right: 0px;

  .all-pictures, .picture-edition {
    display: inline-block;
    vertical-align: top;
    overflow: auto;
    text-align: center;
    position: relative
  }
  .all-pictures {
    width: 13%;
    overflow: hidden;
  }
  .pictures-wrapper {
    position: absolute;
    top: 35px;
    left: 0px;
    right: -20px;
    bottom: 10px;
    padding-right: 25px;
    overflow-y: scroll;
  }
  .picture-edition {
    width: 87%;
    overflow: hidden;
  }
  .img-wrapper {
    display: inline-block;
    position: relative;
    height: auto;
    opacity: 0.5;

    &:hover, &.selected {
      opacity: 1;
    }

    img.min {
      height: auto;
      cursor: pointer;
    }
  }
  .buttons-container {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
  }
}

.picture-edition {
  &__wrapper {
    height: 100%;
    width: 100%;

    > .picture-edition__image,
    > .picture-edition__drawing {
      position: absolute;
      left: -100%;
      right: -100%;
      top: -100%;
      bottom: -100%;
      margin: auto;
      touch-action: none;
    }

    &--taller > .picture-edition__image,
    &--taller > .picture-edition__drawing {
      height: 100%;
      width: auto;
    }

    &--wider > .picture-edition__image,
    &--wider > .picture-edition__drawing {
      height: auto;
      width: 100%;
    }
  }

  &__image,
  &__drawing {
    image-rendering: pixelated;
  }

  &__saving {
    display: none;
  }

  &__loading {
    background-color: rgba(0,0,0,0.8);
    height: 100%;
    position: absolute;
    width: 100%;

    &__logo {
      animation: rotation 2s infinite linear;
      background: #ffffff;
      border-radius: 50px;
      box-shadow: 0px 0px 25px 15px #ffffff;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      width: 60px;
      height: 60px;
      max-width: 20%;
      max-height: 20%;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .pictures-editor {
    position: fixed;
    top: $smartphone-header-height;
    left: $smartphone-nav-width;
  }
}
</style>
